"use client";

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useUiStore } from "@/store/ui";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { currencySymbol } from "@/lib/format";
import {
  Rocket,
  Zap,
  Crown,
  TrendingDown,
  Sparkles,
  Megaphone,
  Star,
  AlertTriangle,
  Check,
  Loader2,
} from "lucide-react";
import type { StoreBoost, UpgradeCalc } from "@/lib/types";

// ============================================================
// V1.0 Monetization — Module 1: Provider "Upgrade to Pro" Card
// ------------------------------------------------------------
// Shows the merchant their current commission tier, an estimate of
// monthly commission paid, and the savings if they upgrade to Pro
// ($99/mo, 7%) or Enterprise ($199/mo, 5%). Includes a "Boost My
// Store" CTA (daily budget $10-$100 → 7-day sponsored storefront).
// ============================================================

const TIER_META: Record<
  string,
  { label: string; rate: number; color: string; icon: React.ElementType }
> = {
  launch_partner: {
    label: "Launch Partner",
    rate: 5,
    color: "text-amber-600",
    icon: Crown,
  },
  standard: {
    label: "Standard",
    rate: 10,
    color: "text-muted-foreground",
    icon: Sparkles,
  },
  premium: {
    label: "Pro",
    rate: 7,
    color: "text-primary",
    icon: Zap,
  },
  enterprise: {
    label: "Enterprise",
    rate: 5,
    color: "text-amber-600",
    icon: Crown,
  },
};

export function UpgradeToProCard() {
  const { t } = useTranslation();
  const navigate = useUiStore((s) => s.navigate);
  const qc = useQueryClient();
  const [boostOpen, setBoostOpen] = useState(false);
  const [dailyBudget, setDailyBudget] = useState("10");

  // Fetch store + boost + upgrade calculator (the /api/services/store/me
  // endpoint now returns all of these in one call).
  const { data, isLoading } = useQuery({
    queryKey: ["store", "me"],
    queryFn: () => api.stores.me(),
    staleTime: 30 * 1000,
  });

  const store = data?.data?.store;
  const boost: StoreBoost | null = data?.data?.boost ?? null;
  const upgradeCalc: UpgradeCalc | undefined = data?.data?.upgradeCalc;
  const commissionTier: string = data?.data?.commissionTier ?? "standard";
  const tierExpiresAt: string | null = data?.data?.tierExpiresAt ?? null;

  const startBoost = useMutation({
    mutationFn: (budget: number) => api.stores.startBoost(budget),
    onSuccess: (res) => {
      if (res.ok) {
        toast.success(`Boost activated! ${currencySymbol()}${budget}/day for 7 days.`);
        setBoostOpen(false);
        qc.invalidateQueries({ queryKey: ["store", "me"] });
        qc.invalidateQueries({ queryKey: ["store", "boost"] });
      } else {
        toast.error(res.error || "Failed to start boost");
      }
    },
  });

  const stopBoost = useMutation({
    mutationFn: () => api.stores.stopBoost(),
    onSuccess: (res) => {
      if (res.ok) {
        toast.success("Boost stopped.");
        qc.invalidateQueries({ queryKey: ["store", "me"] });
        qc.invalidateQueries({ queryKey: ["store", "boost"] });
      } else {
        toast.error(res.error || "Failed to stop boost");
      }
    },
  });

  // Don't render until we have data; also hide if no store yet (the
  // MerchantDashboardView already shows a create-store CTA in that case).
  if (isLoading || !store) return null;

  const tierInfo = TIER_META[commissionTier] ?? TIER_META.standard;
  const TierIcon = tierInfo.icon;
  const isPro = commissionTier === "premium";
  const isEnterprise = commissionTier === "enterprise";
  const isLaunchPartner = commissionTier === "launch_partner";
  const isUpgraded = isPro || isEnterprise || isLaunchPartner;

  // Savings calculation — use the upgradeCalc from backend, or fall back
  // to a simple estimate if not available.
  const monthlyCommission = upgradeCalc?.monthlyCommissionPaid ?? 0;
  const proSavings = upgradeCalc?.proSavings ?? 0;
  const enterpriseSavings = upgradeCalc?.enterpriseSavings ?? 0;
  const proRate = upgradeCalc?.proPlanRate ?? 7;
  const enterpriseRate = upgradeCalc?.enterprisePlanRate ?? 5;
  const proPrice = upgradeCalc?.proPlanPrice ?? 99;
  const enterprisePrice = upgradeCalc?.enterprisePlanPrice ?? 199;

  // Tier expiry warning (Launch Partner)
  const daysToExpiry = tierExpiresAt
    ? Math.max(
        0,
        Math.ceil((new Date(tierExpiresAt).getTime() - Date.now()) / 86400000)
      )
    : null;
  const showExpiryWarning = isLaunchPartner && daysToExpiry !== null && daysToExpiry <= 30;

  // Boost dialog handlers
  const budget = parseInt(dailyBudget, 10) || 10;
  const boostTotal = budget * 7;
  const handleStartBoost = () => {
    if (budget < 10 || budget > 100) {
      toast.error("Daily budget must be between $10 and $100");
      return;
    }
    startBoost.mutate(budget);
  };

  return (
    <>
      <Card className="overflow-hidden border-primary/20 bg-gradient-to-br from-primary/5 via-amber-50/30 to-transparent dark:from-primary/10 dark:via-amber-950/10">
        <CardContent className="p-4 sm:p-5">
          {/* Header row */}
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/15 text-primary">
                <Rocket className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm font-bold uppercase tracking-wide">
                  {t("merchant.boostProfits", "Boost Your Profits")}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t("merchant.monetizationSubtitle", "Commission + Subscription + Ads")}
                </p>
              </div>
            </div>
            <Badge
              variant="outline"
              className={`gap-1 ${tierInfo.color}`}
            >
              <TierIcon className="h-3 w-3" />
              {tierInfo.label} · {tierInfo.rate}%
            </Badge>
          </div>

          {/* Launch Partner expiry warning */}
          {showExpiryWarning && (
            <div className="mt-3 flex items-start gap-2 rounded-md bg-amber-100 p-2 text-xs dark:bg-amber-900/30">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
              <div>
                <span className="font-semibold text-amber-700 dark:text-amber-400">
                  {t("merchant.launchPartnerExpiring", "Launch Partner rate expiring")}
                </span>{" "}
                — {t("merchant.renewInDays", "Renew in {{days}} days to lock in 5%.", { days: daysToExpiry })}
              </div>
            </div>
          )}

          {/* Current fees + savings calculator */}
          <div className="mt-4 rounded-lg border border-border/60 bg-card/60 p-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-xs text-muted-foreground">
                  {t("merchant.currentMonthlyFees", "Current monthly fees")}
                </p>
                <p className="font-bold">
                  {currencySymbol()}{monthlyCommission.toFixed(0)}
                </p>
                <p className="text-[10px] text-muted-foreground">
                  {t("merchant.commissionAtRate", "Commission at {{rate}}%", { rate: tierInfo.rate })}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">
                  {t("merchant.proSavingsLabel", "Pro plan savings")}
                </p>
                <p className={`font-bold ${proSavings > 0 ? "text-emerald-600" : "text-muted-foreground"}`}>
                  {proSavings > 0 ? "−" : ""}{currencySymbol()}{Math.abs(proSavings).toFixed(0)}/mo
                </p>
                <p className="text-[10px] text-muted-foreground">
                  {t("merchant.proPlanRate", "Pro = {{rate}}% + ${{price}}/mo", { rate: proRate, price: proPrice })}
                </p>
              </div>
            </div>

            {/* Upgrade CTA */}
            {!isUpgraded && (
              <Button
                className="mt-3 w-full"
                size="sm"
                onClick={() => navigate("app/subscriptions")}
              >
                <Rocket className="mr-1.5 h-4 w-4" />
                {t("merchant.upgradeToProNow", "Upgrade to Pro now")}
              </Button>
            )}
            {isPro && (
              <Button
                className="mt-3 w-full"
                size="sm"
                variant="outline"
                onClick={() => navigate("app/subscriptions")}
              >
                <Crown className="mr-1.5 h-4 w-4" />
                {t("merchant.upgradeToEnterprise", "Upgrade to Enterprise (5%)")}
                {enterpriseSavings > 0 && (
                  <span className="ml-1 text-emerald-600">
                    save {currencySymbol()}{enterpriseSavings.toFixed(0)}/mo
                  </span>
                )}
              </Button>
            )}
            {isEnterprise && (
              <div className="mt-3 flex items-center justify-center gap-1.5 rounded-md bg-amber-50 py-1.5 text-xs text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                <Crown className="h-3.5 w-3.5" />
                {t("merchant.enterpriseActive", "Enterprise plan active — lowest 5% rate locked in.")}
              </div>
            )}
            {isLaunchPartner && (
              <div className="mt-3 flex items-center justify-center gap-1.5 rounded-md bg-amber-50 py-1.5 text-xs text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                <Crown className="h-3.5 w-3.5" />
                {t("merchant.launchPartnerActive", "Launch Partner active — 5% rate. MOQ: 100 orders/month.")}
              </div>
            )}
          </div>

          {/* Promotions row */}
          <div className="mt-3 grid grid-cols-2 gap-2">
            {/* Boost My Store */}
            <div className="rounded-lg border border-border/60 bg-card/60 p-2.5">
              <div className="flex items-center gap-1.5">
                <Star className="h-3.5 w-3.5 text-amber-500" />
                <p className="text-xs font-semibold">
                  {t("merchant.boostStore", "Boost My Store")}
                </p>
              </div>
              <p className="mt-0.5 text-[10px] text-muted-foreground">
                {t("merchant.boostDesc", "Be first in browse · $10-$100/day")}
              </p>
              {boost?.active ? (
                <div className="mt-2 space-y-1">
                  <Badge className="w-full justify-center bg-emerald-100 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/40 dark:text-emerald-300">
                    <Check className="mr-1 h-3 w-3" /> Active · {boost.daysRemaining}d left
                  </Badge>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 w-full text-xs text-destructive"
                    onClick={() => stopBoost.mutate()}
                    disabled={stopBoost.isPending}
                  >
                    {stopBoost.isPending ? "Stopping..." : t("merchant.stopBoost", "Stop boost")}
                  </Button>
                </div>
              ) : (
                <Button
                  size="sm"
                  variant="secondary"
                  className="mt-2 h-7 w-full text-xs"
                  onClick={() => setBoostOpen(true)}
                >
                  <Zap className="mr-1 h-3 w-3" />
                  {t("merchant.startBoost", "Start boost")}
                </Button>
              )}
            </div>

            {/* Sponsored Ads */}
            <div className="rounded-lg border border-border/60 bg-card/60 p-2.5">
              <div className="flex items-center gap-1.5">
                <Megaphone className="h-3.5 w-3.5 text-primary" />
                <p className="text-xs font-semibold">
                  {t("merchant.sponsoredAds", "Sponsored Ads")}
                </p>
              </div>
              <p className="mt-0.5 text-[10px] text-muted-foreground">
                {t("merchant.sponsoredDesc", "CPM / CPC campaigns in customer feed")}
              </p>
              <Button
                size="sm"
                variant="secondary"
                className="mt-2 h-7 w-full text-xs"
                onClick={() => navigate("app/admin/ads")}
              >
                <Megaphone className="mr-1 h-3 w-3" />
                {t("merchant.manageAds", "Manage ads")}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Boost dialog */}
      <Dialog open={boostOpen} onOpenChange={setBoostOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Star className="h-4 w-4 text-amber-500" />
              {t("merchant.boostYourStore", "Boost Your Store")}
            </DialogTitle>
            <DialogDescription>
              {t("merchant.boostDialogDesc", "Your store appears first in the customer browsing view with a ⭐ Sponsored badge for 7 days.")}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="daily-budget">
                {t("merchant.dailyBudget", "Daily budget")} ({currencySymbol()})
              </Label>
              <Input
                id="daily-budget"
                type="number"
                min={10}
                max={100}
                step={1}
                value={dailyBudget}
                onChange={(e) => setDailyBudget(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                {t("merchant.budgetRange", "Min $10 · Max $100 · 7-day campaign")}
              </p>
            </div>

            {/* Quick presets */}
            <div className="grid grid-cols-4 gap-2">
              {[10, 25, 50, 100].map((preset) => (
                <Button
                  key={preset}
                  size="sm"
                  variant={budget === preset ? "default" : "outline"}
                  className="text-xs"
                  onClick={() => setDailyBudget(String(preset))}
                >
                  ${preset}
                </Button>
              ))}
            </div>

            {/* Summary */}
            <div className="rounded-lg bg-muted/60 p-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t("merchant.dailyBudget", "Daily budget")}</span>
                <span className="font-medium">{currencySymbol()}{budget}/day</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t("merchant.campaignDuration", "Campaign duration")}</span>
                <span className="font-medium">7 {t("merchant.days", "days")}</span>
              </div>
              <div className="mt-1 flex justify-between border-t pt-1">
                <span className="font-semibold">{t("merchant.totalCost", "Total cost")}</span>
                <span className="font-bold text-primary">{currencySymbol()}{boostTotal}</span>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setBoostOpen(false)}>
              {t("common.cancel", "Cancel")}
            </Button>
            <Button onClick={handleStartBoost} disabled={startBoost.isPending}>
              {startBoost.isPending ? (
                <>
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                  {t("merchant.activating", "Activating...")}
                </>
              ) : (
                <>
                  <Rocket className="mr-1 h-4 w-4" />
                  {t("merchant.activateBoost", "Activate boost")}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
